import { RootState } from '../redux/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ILogin, IMenuCounters, ISignInResponse, IUser } from '../models/IUser/IUser';
import { API_BASE_URL } from '../helpers/common';
import { MenuCount } from '../Components/Common/Menu/useLinks/useLinks';
import { IUserState } from '../redux/userSlice';

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      token && headers.set('Authorization', `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (build) => ({
    login: build.mutation<ISignInResponse, any>({
      transformResponse: (response: { data: any }) => response.data,
      query: (data) => ({
        url: '/auth/sign-in',
        method: 'POST',
        body: data,
      }),
    }),
    loginAsClient: build.mutation<ISignInResponse, any>({
      transformResponse: (response: { data: any }) => response.data,
      query: (data) => ({
        url: '/auth/client/sign-in',
        method: 'POST',
        body: data,
      }),
    }),
    forgotPassword: build.mutation<any, { email: string; language: string }>({
      transformResponse: (response: { data: any }) => response.data,
      query: (data) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body: data,
      }),
    }),
    resetPassword: build.mutation<
      any,
      {
        password: string;
        token: string;
      }
    >({
      transformResponse: (response: { data: any }) => response.data,
      query: ({ password, token }) => ({
        url: '/auth/reset-password',
        method: 'POST',
        body: { password, token },
      }),
    }),
    signUpUser: build.mutation<{ signupSessionId: string; userId: number; companyId: number }, any>({
      transformResponse: (response: { data: any }) => response.data,
      query: (data) => ({
        url: '/auth/sign-up/user',
        method: 'POST',
        body: data,
      }),
    }),
    signUpCompany: build.mutation<{ signupSessionId: string }, any>({
      transformResponse: (response: { data: any }) => response.data,
      query: (data) => ({
        url: '/auth/sign-up/company',
        method: 'PATCH',
        body: data,
      }),
    }),
    signUpComplete: build.mutation<any, any>({
      transformResponse: (response: { data: any }) => response.data,
      query: (data) => ({
        url: '/auth/sign-up/complete',
        method: 'PATCH',
        body: data,
      }),
    }),
    isEmailExist: build.mutation<any, any>({
      transformResponse: (response: { data: any }) => response.data,
      query: (data) => ({
        url: '/auth/is-email-exist',
        method: 'POST',
        body: data,
      }),
    }),
    logout: build.mutation<any, string>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
    }),
    switchCompany: build.mutation<{ user: IUser; accessToken: string }, { companyId: number; userId: number }>({
      transformResponse: (response: { data: { user: IUser; accessToken: string } }) => response.data,
      query: (data) => ({
        url: '/auth/switch-company',
        method: 'POST',
        body: data,
      }),
    }),
    switchAccount: build.mutation<{ user: IUser; accessToken: string }, { stockOwnerId: number; userId: number; companyId: number }>({
      transformResponse: (response: { data: { user: IUser; accessToken: string } }) => response.data,
      query: (data) => ({
        url: '/auth/switch-account',
        method: 'POST',
        body: data,
      }),
    }),
    exchangeSupportCode: build.mutation<{ accessToken: string; user: IUser; company: { id: number; name: string } }, { code: string }>({
      transformResponse: (response: { data: any }) => response.data,
      query: (body) => ({
        url: '/auth/support/exchange',
        method: 'POST',
        body,
      }),
    }),
    endSupportSession: build.mutation<void, void>({
      query: () => ({
        url: '/auth/support/end',
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useLoginAsClientMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useSignUpUserMutation,
  useSignUpCompanyMutation,
  useSignUpCompleteMutation,
  useIsEmailExistMutation,
  useLogoutMutation,
  useSwitchCompanyMutation,
  useSwitchAccountMutation,
  useExchangeSupportCodeMutation,
  useEndSupportSessionMutation,
} = authApi;
